import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Download,
  Calendar,
  RefreshCw,
  PieChart as PieChartIcon,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const COLORS = {
  income: '#10B981',
  harvest: '#F59E0B',
  expenses: '#EF4444',
  net: '#3B82F6',
  tithe: '#059669',
  offering: '#0891B2',
  donation: '#7C3AED',
  program: '#EC4899',
  rent: '#DC2626',
  utilities: '#EA580C',
  welfare: '#DB2777',
  evangelism: '#4F46E5',
  maintenance: '#CA8A04'
};

const PIE_COLORS = ['#10B981', '#0891B2', '#7C3AED', '#F59E0B', '#6B7280'];

const FinancialReportsSection = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [exporting, setExporting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [monthlyRes, yearlyRes] = await Promise.all([
        axios.get(`${API}/finance/reports/monthly`, { params: { year: selectedYear } }),
        axios.get(`${API}/finance/reports/yearly`)
      ]);
      setMonthlyData(monthlyRes.data);
      setYearlyData(yearlyRes.data);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
    setLoading(false);
  }, [selectedYear]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/finance/reports/export`, {
        params: { year: selectedYear },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CCC_VOB_Financial_Report_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
    }
    setExporting(false);
  };

  const formatCurrency = (value) => `£${(value || 0).toLocaleString()}`;

  // Prepare pie chart data for income breakdown
  const getIncomeBreakdownData = () => {
    if (!monthlyData) return [];
    const totals = monthlyData.months.reduce((acc, month) => {
      Object.entries(month.income_breakdown || {}).forEach(([key, val]) => {
        acc[key] = (acc[key] || 0) + val;
      });
      return acc;
    }, {});
    
    return Object.entries(totals)
      .filter(([_, value]) => value > 0)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  };

  const getExpenseBreakdownData = () => {
    if (!monthlyData) return [];
    const totals = monthlyData.months.reduce((acc, month) => {
      Object.entries(month.expense_breakdown || {}).forEach(([key, val]) => {
        acc[key] = (acc[key] || 0) + val;
      });
      return acc;
    }, {});
    
    return Object.entries(totals)
      .filter(([_, value]) => value > 0)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-reports-section">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Financial Reports</h2>
              <p className="text-sm text-slate-500">Monthly and yearly financial analysis with charts</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <div className="flex items-center gap-2 bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setSelectedYear(y => y - 1)}
                className="p-2 rounded-lg hover:bg-white transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 font-semibold text-slate-700 min-w-[60px] text-center">
                {selectedYear}
              </span>
              <button
                onClick={() => setSelectedYear(y => y + 1)}
                disabled={selectedYear >= new Date().getFullYear()}
                className="p-2 rounded-lg hover:bg-white transition-all disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Export Button */}
            <motion.button
              onClick={handleExport}
              disabled={exporting}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium shadow-lg flex items-center gap-2 disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {exporting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Export Excel
            </motion.button>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 mt-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'trends', label: 'Trends', icon: TrendingUp },
            { id: 'breakdown', label: 'Breakdown', icon: PieChartIcon }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeView === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Yearly Summary Cards */}
      {monthlyData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg"
          >
            <div className="flex items-center gap-2 mb-2 text-emerald-100">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Total Income</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(monthlyData.yearly_totals.total_income)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-5 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg"
          >
            <div className="flex items-center gap-2 mb-2 text-amber-100">
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Harvest</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(monthlyData.yearly_totals.harvest)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-5 rounded-2xl bg-gradient-to-br from-red-500 to-rose-500 text-white shadow-lg"
          >
            <div className="flex items-center gap-2 mb-2 text-red-100">
              <TrendingDown className="w-4 h-4" />
              <span className="text-sm">Expenses</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(monthlyData.yearly_totals.expenses)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`p-5 rounded-2xl shadow-lg text-white ${
              monthlyData.yearly_totals.net >= 0
                ? 'bg-gradient-to-br from-blue-500 to-indigo-600'
                : 'bg-gradient-to-br from-slate-600 to-slate-800'
            }`}
          >
            <div className="flex items-center gap-2 mb-2 text-blue-100">
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm">Net Balance</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(monthlyData.yearly_totals.net)}</p>
          </motion.div>
        </div>
      )}

      {/* Overview View */}
      {activeView === 'overview' && monthlyData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
        >
          <h3 className="font-bold text-slate-800 mb-6">Monthly Income vs Expenses - {selectedYear}</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData.months}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748B', fontSize: 12 }} tickFormatter={(v) => `£${v}`} />
                <Tooltip
                  formatter={(value) => [`£${value.toLocaleString()}`, '']}
                  contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}
                />
                <Legend />
                <Bar dataKey="income" name="Regular Income" fill={COLORS.income} radius={[4, 4, 0, 0]} />
                <Bar dataKey="harvest" name="Harvest" fill={COLORS.harvest} radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill={COLORS.expenses} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Trends View */}
      {activeView === 'trends' && (
        <div className="space-y-6">
          {/* Net Balance Trend */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
          >
            <h3 className="font-bold text-slate-800 mb-6">Net Balance Trend - {selectedYear}</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyData?.months || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fill: '#64748B', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 12 }} tickFormatter={(v) => `£${v}`} />
                  <Tooltip
                    formatter={(value) => [`£${value.toLocaleString()}`, '']}
                    contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total_income"
                    name="Total Income"
                    stroke={COLORS.income}
                    strokeWidth={3}
                    dot={{ fill: COLORS.income, r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="expenses"
                    name="Expenses"
                    stroke={COLORS.expenses}
                    strokeWidth={3}
                    dot={{ fill: COLORS.expenses, r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="net"
                    name="Net Balance"
                    stroke={COLORS.net}
                    strokeWidth={3}
                    dot={{ fill: COLORS.net, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Year-over-Year Comparison */}
          {yearlyData && yearlyData.years.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
            >
              <h3 className="font-bold text-slate-800 mb-6">Year-over-Year Comparison</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyData.years}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="year" tick={{ fill: '#64748B', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748B', fontSize: 12 }} tickFormatter={(v) => `£${v}`} />
                    <Tooltip
                      formatter={(value) => [`£${value.toLocaleString()}`, '']}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0' }}
                    />
                    <Legend />
                    <Bar dataKey="total_income" name="Total Income" fill={COLORS.income} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expenses" name="Expenses" fill={COLORS.expenses} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="net" name="Net Balance" fill={COLORS.net} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Breakdown View */}
      {activeView === 'breakdown' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income Breakdown */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
          >
            <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Income Breakdown - {selectedYear}
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getIncomeBreakdownData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {getIncomeBreakdownData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {getIncomeBreakdownData().map((item, index) => (
                <div key={item.name} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                    />
                    <span className="text-sm text-slate-600">{item.name}</span>
                  </div>
                  <span className="font-semibold text-slate-800">{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Expense Breakdown */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
          >
            <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-500" />
              Expense Breakdown - {selectedYear}
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getExpenseBreakdownData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {getExpenseBreakdownData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#EF4444', '#F97316', '#F59E0B', '#84CC16', '#6B7280'][index % 5]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `£${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {getExpenseBreakdownData().map((item, index) => (
                <div key={item.name} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: ['#EF4444', '#F97316', '#F59E0B', '#84CC16', '#6B7280'][index % 5] }}
                    />
                    <span className="text-sm text-slate-600">{item.name}</span>
                  </div>
                  <span className="font-semibold text-slate-800">{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Monthly Summary Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100"
      >
        <h3 className="font-bold text-slate-800 mb-4">Monthly Summary - {selectedYear}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Month</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-emerald-600">Income</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-amber-600">Harvest</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-red-600">Expenses</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-blue-600">Net</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData?.months.map((month, index) => (
                <tr key={month.month} className={index % 2 === 0 ? 'bg-slate-50' : ''}>
                  <td className="py-3 px-4 font-medium text-slate-800">{month.month}</td>
                  <td className="py-3 px-4 text-right text-emerald-600">{formatCurrency(month.income)}</td>
                  <td className="py-3 px-4 text-right text-amber-600">{formatCurrency(month.harvest)}</td>
                  <td className="py-3 px-4 text-right text-red-600">{formatCurrency(month.expenses)}</td>
                  <td className={`py-3 px-4 text-right font-semibold ${month.net >= 0 ? 'text-blue-600' : 'text-slate-600'}`}>
                    {formatCurrency(month.net)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold">
                <td className="py-3 px-4 text-slate-800">TOTAL</td>
                <td className="py-3 px-4 text-right text-emerald-700">{formatCurrency(monthlyData?.yearly_totals.income)}</td>
                <td className="py-3 px-4 text-right text-amber-700">{formatCurrency(monthlyData?.yearly_totals.harvest)}</td>
                <td className="py-3 px-4 text-right text-red-700">{formatCurrency(monthlyData?.yearly_totals.expenses)}</td>
                <td className={`py-3 px-4 text-right ${monthlyData?.yearly_totals.net >= 0 ? 'text-blue-700' : 'text-slate-700'}`}>
                  {formatCurrency(monthlyData?.yearly_totals.net)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default FinancialReportsSection;
